'use client';
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { appointmentService } from '@/lib/services/appointmentService';
import Icon from '@/components/ui/AppIcon';

interface AvailableSlot {
  time: string;
}

export default function BookingFormSection() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    appointmentDate: '',
    appointmentTime: '',
    location: 'office',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [availableSlots, setAvailableSlots] = useState<AvailableSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        email: user.email || ''
      }));
    }
  }, [user]);

  useEffect(() => {
    if (formData.appointmentDate) {
      fetchAvailableSlots();
    } else {
      setAvailableSlots([]);
    }
  }, [formData.appointmentDate]);

  const fetchAvailableSlots = async () => {
    setLoadingSlots(true);
    try {
      const eventTypeId = process.env.NEXT_PUBLIC_CAL_COM_EVENT_TYPE_ID;
      if (!eventTypeId) {
        console.warn('Cal.com event type ID not configured');
        setLoadingSlots(false);
        return;
      }

      const selectedDate = new Date(formData.appointmentDate);
      const startTime = new Date(selectedDate);
      startTime.setHours(0, 0, 0, 0);
      
      const endTime = new Date(selectedDate);
      endTime.setHours(23, 59, 59, 999);

      const response = await fetch(
        `/api/calcom/slots?eventTypeId=${eventTypeId}&startTime=${startTime.toISOString()}&endTime=${endTime.toISOString()}&timeZone=${Intl.DateTimeFormat().resolvedOptions().timeZone}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch available slots');
      }

      const data = await response.json();
      if (data.success && data.data) {
        setAvailableSlots(data.data);
      }
    } catch (error) {
      console.error('Error fetching available slots:', error);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const eventTypeId = process.env.NEXT_PUBLIC_CAL_COM_EVENT_TYPE_ID;
      
      if (eventTypeId) {
        const appointmentDateTime = new Date(`${formData.appointmentDate}T${formData.appointmentTime}`);
        
        const calcomResponse = await fetch('/api/calcom/bookings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            start: appointmentDateTime.toISOString(),
            attendee: {
              name: formData.fullName,
              email: formData.email,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              phoneNumber: formData.phone,
              language: 'en',
            },
            eventTypeId: parseInt(eventTypeId),
            location: { type: formData.location === 'office' ? 'inPerson' : 'phone' },
            metadata: {
              notes: formData.notes,
              source: 'website',
            },
          }),
        });

        const calcomData = await calcomResponse.json();
        
        if (!calcomData.success) {
          throw new Error(calcomData.error || 'Failed to create Cal.com booking');
        }
      }

      const result = await appointmentService.createAppointment(formData);

      if (result) {
        setSuccess(true);
        setFormData({
          fullName: '',
          email: user?.email || '',
          phone: '',
          appointmentDate: '',
          appointmentTime: '',
          location: 'office',
          notes: ''
        });
        setAvailableSlots([]);
      } else {
        setError('Failed to book appointment. Please try again.');
      }
    } catch (err: any) {
      console.error('Booking error:', err);
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const formatSlotTime = (isoTime: string) => {
    const date = new Date(isoTime);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <div className="container-card p-8">
      <div className="max-w-2xl mx-auto">
        <h2 className="text-3xl font-bold text-foreground mb-2">Book an Appointment</h2>
        <p className="text-muted-foreground mb-8">
          Schedule a consultation to discuss your container needs
        </p>

        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
            <Icon name="CheckCircleIcon" size={24} className="text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-green-800 mb-1">Appointment Booked Successfully!</h3>
              <p className="text-sm text-green-700">
                We've sent a confirmation email to {formData.email}. We'll see you soon!
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <Icon name="ExclamationCircleIcon" size={24} className="text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-800 mb-1">Booking Failed</h3>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-foreground mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="John Doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="john@example.com"
              />
            </div>
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="(555) 123-4567"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="appointmentDate" className="block text-sm font-medium text-foreground mb-2">
                Preferred Date *
              </label>
              <input
                type="date"
                id="appointmentDate"
                name="appointmentDate"
                value={formData.appointmentDate}
                onChange={handleChange}
                min={getMinDate()}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            <div>
              <label htmlFor="appointmentTime" className="block text-sm font-medium text-foreground mb-2">
                Preferred Time *
              </label>
              {loadingSlots ? (
                <div className="w-full px-4 py-3 border border-gray-300 rounded-lg flex items-center justify-center">
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin text-primary mr-2" />
                  <span className="text-sm text-muted-foreground">Loading available times...</span>
                </div>
              ) : availableSlots.length > 0 ? (
                <select
                  id="appointmentTime"
                  name="appointmentTime"
                  value={formData.appointmentTime}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Select a time</option>
                  {availableSlots.map((slot, index) => {
                    const slotDate = new Date(slot.time);
                    const timeValue = slotDate.toTimeString().slice(0, 5);
                    return (
                      <option key={index} value={timeValue}>
                        {formatSlotTime(slot.time)}
                      </option>
                    );
                  })}
                </select>
              ) : formData.appointmentDate ? (
                <div className="w-full px-4 py-3 border border-gray-300 rounded-lg">
                  <p className="text-sm text-muted-foreground">No available slots for this date</p>
                </div>
              ) : (
                <input
                  type="time"
                  id="appointmentTime"
                  name="appointmentTime"
                  value={formData.appointmentTime}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              )}
            </div>
          </div>

          <div>
            <label htmlFor="location" className="block text-sm font-medium text-foreground mb-2">
              Consultation Method *
            </label>
            <select
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="office">In-Person (Office Visit)</option>
              <option value="phone">Phone Call</option>
              <option value="video">Video Conference</option>
            </select>
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-foreground mb-2">
              Additional Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Tell us about your container needs..."
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                Booking...
              </>
            ) : (
              <>
                <Icon name="CalendarIcon" size={20} />
                Book Appointment
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}